import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, unref as _unref, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, normalizeStyle as _normalizeStyle } from "vue"

const _hoisted_1 = { class: "mb-0 mt-2 text-sm leading-tight text-gray-500" }
const _hoisted_2 = {
  key: 0,
  class: "flex h-80 items-center justify-center"
}
const _hoisted_3 = {
  key: 1,
  class: "flex h-64 items-center justify-center text-sm text-gray-500"
}
const _hoisted_4 = { key: 2 }
const _hoisted_5 = { class: "relative mx-auto h-80 max-w-4xl" }
const _hoisted_6 = {
  key: 0,
  class: "country-arrow absolute inset-0 z-10 h-full w-full pointer-events-none",
  "aria-hidden": "true"
}
const _hoisted_7 = {
  id: "country-arrow-head",
  markerWidth: "8",
  markerHeight: "8",
  refX: "7",
  refY: "4",
  orient: "auto",
  markerUnits: "strokeWidth"
}
const _hoisted_8 = ["fill"]
const _hoisted_9 = ["x1", "y1", "x2", "y2", "stroke"]
const _hoisted_10 = {
  key: 1,
  class: "country-popover absolute z-20 w-[292px] rounded-lg border border-gray-200 bg-white p-3"
}
const _hoisted_11 = { class: "mb-3 flex items-start justify-between gap-3" }
const _hoisted_12 = { class: "text-sm font-semibold text-black" }
const _hoisted_13 = { class: "text-xs text-gray-500" }
const _hoisted_14 = {
  key: 0,
  class: "space-y-3"
}
const _hoisted_15 = {
  key: 1,
  class: "py-5 text-center text-sm text-gray-500"
}
const _hoisted_16 = {
  key: 2,
  class: "space-y-3"
}
const _hoisted_17 = { class: "h-10 w-10 shrink-0 overflow-hidden rounded bg-gray-100 ring-1 ring-gray-200" }
const _hoisted_18 = ["src", "alt", "onError"]
const _hoisted_19 = {
  key: 1,
  class: "flex h-full w-full items-center justify-center text-xs font-semibold text-gray-500"
}
const _hoisted_20 = { class: "min-w-0 flex-1" }
const _hoisted_21 = { class: "truncate text-sm font-medium text-black" }
const _hoisted_22 = { class: "truncate text-xs text-gray-500" }
const _hoisted_23 = { class: "text-right" }
const _hoisted_24 = { class: "text-sm font-semibold text-black" }
const _hoisted_25 = { class: "text-xs text-gray-500" }
const _hoisted_26 = { class: "mt-5 flex flex-wrap justify-center gap-x-5 gap-y-2" }

import { computed, reactive, ref, watch } from 'vue'
import { Pie } from 'vue-chartjs'
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
} from 'chart.js'
import type { ChartData, ChartOptions, TooltipItem } from 'chart.js'
import type { RepartitionClientsPaysApi } from '@/types'
import { CommandesPrestationsApi } from '@/api/commandesPrestations'

type CountryProduct = {
  id_produit: number
  reference: string | null
  imageUrl?: string | null
  categorie?: string | null
  quantite: number
  chiffre_affaires: number
}

type GroupedCountry = RepartitionClientsPaysApi & {
  countryIds?: number[]
}

const ARROW_COLOR = '#64748b'


export default /*@__PURE__*/_defineComponent({
  __name: 'CountryClientsChart',
  props: {
    rows: { default: () => [] },
    loading: { type: Boolean, default: false },
    periodLabel: {},
    frameless: { type: Boolean, default: false },
    sellerId: { default: null },
    range: { default: 'mois' }
  },
  setup(__props: any) {

ChartJS.register(ArcElement, Tooltip, Legend)

const props = __props

const selectedCountry = ref<GroupedCountry | null>(null)
const countryProducts = ref<CountryProduct[]>([])
const isLoadingProducts = ref(false)
const failedImages = reactive<Record<number, boolean>>({})
const arrowLine = ref<{
  x1: number
  y1: number
  x2: number
  y2: number
  color: string
} | null>(null)

const colors = [
  '#e11d48',
  '#0f2a66',
  '#f0ddb7',
  '#b8cfc6',
  '#c8b58b',
  '#9fd3bd',
  '#f1d38a',
  '#cf758c',
  '#dfc28a',
]

const totalClients = computed(() =>
  props.rows.reduce((sum, row) => sum + Number(row.clients || 0), 0),
)

const groupedRows = computed<GroupedCountry[]>(() => {
  const cleaned = props.rows
    .map((row) => ({
      ...row,
      pays: row.pays || 'Non renseigné',
      clients: Number(row.clients || 0),
    }))
    .filter((row) => row.clients > 0)
    .sort((a, b) => b.clients - a.clients)

  if (cleaned.length <= 8) return cleaned

  const visible = cleaned.slice(0, 8)
  const hiddenRows = cleaned.slice(8)
  const autres = hiddenRows.reduce((sum, row) => sum + row.clients, 0)
  const countryIds = hiddenRows
    .map((row) => row.id_pays)
    .filter((id): id is number => typeof id === 'number' && id > 0)
  if (autres > 0) {
    visible.push({
      id_pays: null,
      pays: 'Autres',
      clients: autres,
      countryIds,
    })
  }
  return visible
})

const formatPercent = (value: number) => {
  if (!totalClients.value) return '0,00%'
  const percent = (Number(value || 0) / totalClients.value) * 100
  return `${percent.toLocaleString('fr-FR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}%`
}

const formatCurrency = (value: number) =>
  `${Number(value || 0).toLocaleString('fr-FR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} €`

const getInitial = (reference: string | null) =>
  (reference || '?').trim().charAt(0).toUpperCase() || '?'

const closePopover = () => {
  selectedCountry.value = null
  countryProducts.value = []
  isLoadingProducts.value = false
  arrowLine.value = null
}

const loadCountryProducts = async (country: GroupedCountry) => {
  const countryIds = country.countryIds ?? []
  if (!props.sellerId || (country.id_pays == null && countryIds.length === 0)) return

  isLoadingProducts.value = true
  countryProducts.value = []
  try {
    countryProducts.value = await CommandesPrestationsApi.getBestReferencesByCountry(
      props.sellerId,
      country.id_pays ?? null,
      props.range,
      5,
      countryIds,
    )
  } catch {
    countryProducts.value = []
  } finally {
    isLoadingProducts.value = false
  }
}

const handleSegmentClick = async (event: any, elements: any[], chart: any) => {
  if (!elements.length) {
    closePopover()
    return
  }

  const index = elements[0]?.index
  const country = groupedRows.value[index]
  const countryIds = country?.countryIds ?? []
  if (!country || !props.sellerId || (country.id_pays == null && countryIds.length === 0)) {
    closePopover()
    return
  }

  const element = elements[0]?.element
  const position = element?.tooltipPosition?.() ?? element
  const startX = typeof position?.x === 'number' ? position.x : chart.width / 2
  const startY = typeof position?.y === 'number' ? position.y : chart.height / 2
  const popoverLeft = chart.width - 4 - 292
  const popoverTop = -8

  arrowLine.value = {
    x1: startX,
    y1: startY,
    x2: popoverLeft - 8,
    y2: popoverTop + 36,
    color: ARROW_COLOR,
  }
  selectedCountry.value = country
  await loadCountryProducts(country)
}

const chartData = computed<ChartData<'pie'>>(() => ({
  labels: groupedRows.value.map((row) => `${row.pays}: ${formatPercent(row.clients)}`),
  datasets: [
    {
      data: groupedRows.value.map((row) => row.clients),
      backgroundColor: groupedRows.value.map((_, index) => colors[index % colors.length]),
      borderColor: '#ffffff',
      borderWidth: 2,
      hoverOffset: 18,
      offset: groupedRows.value.map((_, index) => (index === 0 ? 14 : 0)),
    },
  ],
}))

const chartOptions = computed<ChartOptions<'pie'>>(() => ({
  responsive: true,
  maintainAspectRatio: false,
  layout: {
    padding: {
      top: 16,
      right: 24,
      bottom: 8,
      left: 24,
    },
  },
  plugins: {
    legend: {
      display: false,
    },
    tooltip: {
      backgroundColor: 'rgba(0, 0, 0, 0.85)',
      titleColor: '#ffffff',
      bodyColor: '#ffffff',
      padding: 10,
      callbacks: {
        label(context: TooltipItem<'pie'>) {
          const value = Number(context.parsed || 0)
          return `${context.label}: ${value} client${value > 1 ? 's' : ''}`
        },
      },
    },
  },
  onClick: handleSegmentClick,
  onHover(event: any, elements: any[], chart: any) {
    if (chart?.canvas) {
      chart.canvas.style.cursor = elements.length ? 'pointer' : 'default'
    }
  },
}))

watch(
  () => [props.rows, props.range, props.sellerId],
  () => closePopover(),
)

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("section", {
    class: _normalizeClass([
      props.frameless
        ? 'bg-transparent'
        : 'bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden'
    ])
  }, [
    _createElementVNode("div", {
      class: _normalizeClass(props.frameless ? 'pt-1 pb-2' : 'px-6 pt-5 pb-4 border-b border-gray-200')
    }, [
      _cache[0] || (_cache[0] = _createElementVNode("h2", { class: "mb-0 text-xl font-semibold leading-tight text-black" }, " Répartition clients par pays ", -1)),
      _createElementVNode("p", _hoisted_1, _toDisplayString(_ctx.periodLabel), 1)
    ], 2),
    _createElementVNode("div", {
      class: _normalizeClass(props.frameless ? 'pt-4 pb-3' : 'px-6 py-5')
    }, [
      (_ctx.loading)
        ? (_openBlock(), _createElementBlock("div", _hoisted_2, _cache[1] || (_cache[1] = [
            _createElementVNode("div", { class: "flex flex-col items-center gap-3" }, [
              _createElementVNode("div", { class: "h-10 w-10 animate-spin rounded-full border-4 border-blue-500 border-t-transparent" }),
              _createElementVNode("span", { class: "text-sm font-medium text-gray-500" }, "Chargement de la répartition...")
            ], -1)
          ])))
        : (groupedRows.value.length === 0)
          ? (_openBlock(), _createElementBlock("div", _hoisted_3, " Aucune donnée client sur cette période. "))
          : (_openBlock(), _createElementBlock("div", _hoisted_4, [
              _createElementVNode("div", _hoisted_5, [
                _createVNode(_unref(Pie), {
                  data: chartData.value,
                  options: chartOptions.value
                }, null, 8, ["data", "options"]),
                (selectedCountry.value && arrowLine.value)
                  ? (_openBlock(), _createElementBlock("svg", _hoisted_6, [
                      _createElementVNode("defs", null, [
                        _createElementVNode("marker", _hoisted_7, [
                          _createElementVNode("path", {
                            d: "M 0 0 L 8 4 L 0 8 z",
                            fill: arrowLine.value.color
                          }, null, 8, _hoisted_8)
                        ])
                      ]),
                      _createElementVNode("line", {
                        x1: arrowLine.value.x1,
                        y1: arrowLine.value.y1,
                        x2: arrowLine.value.x2,
                        y2: arrowLine.value.y2,
                        stroke: arrowLine.value.color,
                        "stroke-width": "1.5",
                        "stroke-linecap": "round",
                        "stroke-dasharray": "5 5",
                        "marker-end": "url(#country-arrow-head)"
                      }, null, 8, _hoisted_9)
                    ]))
                  : _createCommentVNode("", true),
                (selectedCountry.value)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
                      _createElementVNode("div", _hoisted_11, [
                        _createElementVNode("div", null, [
                          _createElementVNode("div", _hoisted_12, _toDisplayString(selectedCountry.value.pays), 1),
                          _createElementVNode("div", _hoisted_13, " Top produits vendus · " + _toDisplayString(formatPercent(selectedCountry.value.clients)), 1)
                        ]),
                        _createElementVNode("button", {
                          type: "button",
                          class: "flex h-6 w-6 items-center justify-center rounded-full text-sm text-gray-400 hover:bg-gray-100 hover:text-black",
                          onClick: closePopover
                        }, " × ")
                      ]),
                      (isLoadingProducts.value)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_14, [
                            (_openBlock(), _createElementBlock(_Fragment, null, _renderList(3, (index) => {
                              return _createElementVNode("div", {
                                key: index,
                                class: "flex animate-pulse items-center gap-3"
                              }, _cache[2] || (_cache[2] = [
                                _createElementVNode("div", { class: "h-10 w-10 rounded bg-gray-200" }, null, -1),
                                _createElementVNode("div", { class: "min-w-0 flex-1" }, [
                                  _createElementVNode("div", { class: "mb-2 h-3 w-2/3 rounded bg-gray-200" }),
                                  _createElementVNode("div", { class: "h-2.5 w-1/2 rounded bg-gray-100" })
                                ], -1)
                              ]))
                            }), 64))
                          ]))
                        : (countryProducts.value.length === 0)
                          ? (_openBlock(), _createElementBlock("div", _hoisted_15, " Aucun produit trouvé. "))
                          : (_openBlock(), _createElementBlock("div", _hoisted_16, [
                              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(countryProducts.value, (product) => {
                                return (_openBlock(), _createElementBlock("article", {
                                  key: product.id_produit,
                                  class: "flex items-center gap-3"
                                }, [
                                  _createElementVNode("div", _hoisted_17, [
                                    (product.imageUrl && !failedImages[product.id_produit])
                                      ? (_openBlock(), _createElementBlock("img", {
                                          key: 0,
                                          src: product.imageUrl,
                                          alt: product.reference || 'Produit',
                                          class: "h-full w-full object-cover",
                                          onError: ($event: any) => (failedImages[product.id_produit] = true)
                                        }, null, 40, _hoisted_18))
                                      : (_openBlock(), _createElementBlock("div", _hoisted_19, _toDisplayString(getInitial(product.reference)), 1))
                                  ]),
                                  _createElementVNode("div", _hoisted_20, [
                                    _createElementVNode("div", _hoisted_21, _toDisplayString(product.reference || 'Produit'), 1),
                                    _createElementVNode("div", _hoisted_22, _toDisplayString(product.categorie || 'Sans catégorie'), 1)
                                  ]),
                                  _createElementVNode("div", _hoisted_23, [
                                    _createElementVNode("div", _hoisted_24, _toDisplayString(product.quantite), 1),
                                    _createElementVNode("div", _hoisted_25, _toDisplayString(formatCurrency(product.chiffre_affaires)), 1)
                                  ])
                                ]))
                              }), 128))
                            ]))
                    ]))
                  : _createCommentVNode("", true)
              ]),
              _createElementVNode("div", _hoisted_26, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(groupedRows.value, (row, index) => {
                  return (_openBlock(), _createElementBlock("div", {
                    key: row.pays,
                    class: "inline-flex items-center gap-2 text-sm text-black"
                  }, [
                    _createElementVNode("span", {
                      class: "h-3 w-6 rounded-sm",
                      style: _normalizeStyle({ backgroundColor: colors[index % colors.length] })
                    }, null, 4),
                    _createElementVNode("span", null, _toDisplayString(row.pays) + " " + _toDisplayString(formatPercent(row.clients)), 1)
                  ]))
                }), 128))
              ])
            ]))
    ], 2)
  ], 2))
}
}

})